import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Wrench, MapPin, Clock, User, Truck, RefreshCw, CheckCircle, Building, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function RepairsPage() {
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    fetchRepairs();
  }, []);

  const fetchRepairs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('vaultai_token');
      const response = await axios.get(`${API}/repairs?status=pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRepairs(response.data.repairs || []);
    } catch (error) {
      console.error('Failed to fetch repairs:', error);
      toast.error('Failed to load repair requests');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (repairId, status) => {
    setUpdating(repairId);
    try {
      const token = localStorage.getItem('vaultai_token');
      await axios.patch(`${API}/repairs/${repairId}`, 
        { status },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`Status updated to: ${getStatusLabel(status)}`);
      fetchRepairs();
    } catch (error) {
      console.error('Failed to update repair:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Pending',
      'fix_asap': 'Fix ASAP',
      'wait_for_shop': 'Wait for Shop',
      'wait_until_tomorrow': 'Wait Until Tomorrow',
      'resolved': 'Resolved'
    };
    return labels[status] || status;
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      'fix_asap': 'bg-red-500/20 text-red-400 border-red-500/30',
      'wait_for_shop': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'wait_until_tomorrow': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      'resolved': 'bg-green-500/20 text-green-400 border-green-500/30'
    };
    return (
      <Badge variant="outline" className={styles[status] || ''}>
        {getStatusLabel(status)}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="space-y-6" data-testid="repairs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wrench className="w-6 h-6 text-amber-500" />
            Repair Requests
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage repair requests from drivers
          </p>
        </div>
        <Button onClick={fetchRepairs} variant="outline" className="gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold text-amber-400">
                  {repairs.filter(r => r.status === 'pending').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-amber-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Fix ASAP</p>
                <p className="text-2xl font-bold text-red-400">
                  {repairs.filter(r => r.status === 'fix_asap').length}
                </p>
              </div>
              <Wrench className="w-8 h-8 text-red-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Wait for Shop</p>
                <p className="text-2xl font-bold text-blue-400">
                  {repairs.filter(r => r.status === 'wait_for_shop').length}
                </p>
              </div>
              <Building className="w-8 h-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-500/10 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Wait Tomorrow</p>
                <p className="text-2xl font-bold text-purple-400">
                  {repairs.filter(r => r.status === 'wait_until_tomorrow').length}
                </p>
              </div>
              <Calendar className="w-8 h-8 text-purple-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Repairs List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : repairs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <p className="text-lg font-medium">No pending repairs</p>
            <p className="text-muted-foreground">All repair requests have been handled</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {repairs.map((repair) => (
            <Card key={repair.id} className="hover:border-amber-500/50 transition-colors">
              <CardContent className="p-5">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Repair Info */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      {getStatusBadge(repair.status)}
                      <span className="text-xs text-muted-foreground">
                        {formatDate(repair.created_at)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">{repair.driver_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-muted-foreground" />
                        <span>Unit {repair.unit_number}</span>
                      </div>
                    </div>
                    
                    <div className="bg-secondary/50 rounded-lg p-3">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Issue:</p>
                      <p className="text-foreground">{repair.issue}</p>
                    </div>
                    
                    {repair.location && repair.location !== 'Unknown' && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-amber-500" />
                          <span className="font-medium">{repair.location}</span>
                        </div>
                        {repair.gps_address && repair.gps_address !== 'Unknown' && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground ml-6">
                            <span>GPS: {repair.gps_address}</span>
                          </div>
                        )}
                        {repair.latitude && repair.longitude && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground ml-6">
                            <span>Coords: {repair.latitude.toFixed(4)}, {repair.longitude.toFixed(4)}</span>
                            <a 
                              href={`https://www.google.com/maps?q=${repair.latitude},${repair.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 underline"
                            >
                              View Map
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2 lg:min-w-[200px]">
                    <Button
                      variant={repair.status === 'fix_asap' ? 'default' : 'outline'}
                      className={repair.status === 'fix_asap' 
                        ? 'bg-red-600 hover:bg-red-700' 
                        : 'border-red-500/50 text-red-400 hover:bg-red-500/10'}
                      onClick={() => updateStatus(repair.id, 'fix_asap')}
                      disabled={updating === repair.id}
                    >
                      {updating === repair.id ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
                      Fix ASAP
                    </Button>
                    <Button
                      variant={repair.status === 'wait_for_shop' ? 'default' : 'outline'}
                      className={repair.status === 'wait_for_shop' 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'border-blue-500/50 text-blue-400 hover:bg-blue-500/10'}
                      onClick={() => updateStatus(repair.id, 'wait_for_shop')}
                      disabled={updating === repair.id}
                    >
                      Wait for Shop
                    </Button>
                    <Button
                      variant={repair.status === 'wait_until_tomorrow' ? 'default' : 'outline'}
                      className={repair.status === 'wait_until_tomorrow' 
                        ? 'bg-purple-600 hover:bg-purple-700' 
                        : 'border-purple-500/50 text-purple-400 hover:bg-purple-500/10'}
                      onClick={() => updateStatus(repair.id, 'wait_until_tomorrow')}
                      disabled={updating === repair.id}
                    >
                      Wait Until Tomorrow
                    </Button>
                    <Button
                      variant="outline"
                      className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                      onClick={() => updateStatus(repair.id, 'resolved')}
                      disabled={updating === repair.id}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark Resolved
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
